<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import yandexClient from 'dashboard/api/channel/yandexClient';
import router from '../../../../../index';
import SettingsSubPageHeader from '../../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

defineOptions({
  name: 'YandexOAuthChannel',
});

const { t } = useI18n();

const isRequestingAuthorization = ref(false);

// idle -> waiting (link handed out) -> expired or taken (the mailbox is in
// another account); the completed case navigates away, so it never renders.
const inviteState = ref('idle');
const takenEmail = ref('');
const inviteUrl = ref('');
const isCreatingInvite = ref(false);

let pollTimer = null;

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

// The admin authorizes their own mailbox right here, in this browser.
async function requestAuthorization() {
  try {
    isRequestingAuthorization.value = true;
    const {
      data: { url },
    } = await yandexClient.generateAuthorization();
    window.location.href = url;
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.YANDEX.ERROR_MESSAGE'));
  } finally {
    isRequestingAuthorization.value = false;
  }
}

async function copyToClipboard(value) {
  // Only available over https/localhost; without it the link is still on
  // screen, so this degrades to selecting it by hand.
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (error) {
    return false;
  }
}

async function pollInvite(token) {
  try {
    const { data } = await yandexClient.getInvite(token);

    if (data.status === 'completed') {
      stopPolling();
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: data.inbox_id },
      });
    } else if (data.status === 'taken') {
      stopPolling();
      takenEmail.value = data.email;
      inviteState.value = 'taken';
    } else if (data.status === 'expired') {
      stopPolling();
      inviteState.value = 'expired';
    }
  } catch (error) {
    stopPolling();
    inviteState.value = 'expired';
  }
}

async function createInvite() {
  isCreatingInvite.value = true;
  try {
    const { data } = await yandexClient.createInvite();
    inviteUrl.value = data.url;
    inviteState.value = 'waiting';

    if (await copyToClipboard(data.url)) {
      useAlert(t('INBOX_MGMT.ADD.YANDEX.INVITE.COPIED'));
    }

    stopPolling();
    pollTimer = setInterval(() => pollInvite(data.token), 3000);
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.YANDEX.INVITE.ERROR'));
  } finally {
    isCreatingInvite.value = false;
  }
}

onBeforeUnmount(stopPolling);
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 overflow-y-auto">
    <SettingsSubPageHeader
      :header-title="$t('INBOX_MGMT.ADD.YANDEX.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.YANDEX.DESCRIPTION')"
    />

    <NextButton
      class="mt-6"
      :is-loading="isRequestingAuthorization"
      solid
      blue
      :label="$t('INBOX_MGMT.ADD.YANDEX.SIGN_IN')"
      @click="requestAuthorization"
    />

    <div class="mt-8 pt-6 border-t border-n-weak max-w-xl">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.ADD.YANDEX.INVITE.TITLE') }}
      </h3>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.YANDEX.INVITE.DESCRIPTION') }}
      </p>

      <NextButton
        class="mt-4"
        :is-loading="isCreatingInvite"
        faded
        blue
        :label="$t('INBOX_MGMT.ADD.YANDEX.INVITE.CREATE')"
        @click="createInvite"
      />

      <template v-if="inviteState === 'waiting'">
        <div class="mt-4 flex items-center gap-2 text-sm text-n-slate-11">
          <span
            class="w-3 h-3 rounded-full border-2 border-n-brand border-t-transparent animate-spin"
          />
          {{ $t('INBOX_MGMT.ADD.YANDEX.INVITE.WAITING') }}
        </div>
        <input
          :value="inviteUrl"
          readonly
          class="mt-2 w-full text-xs"
          @focus="$event.target.select()"
        />
      </template>

      <p
        v-else-if="inviteState === 'expired'"
        class="mt-4 text-sm text-n-ruby-9"
      >
        {{ $t('INBOX_MGMT.ADD.YANDEX.INVITE.EXPIRED') }}
      </p>
      <p v-else-if="inviteState === 'taken'" class="mt-4 text-sm text-n-ruby-9">
        {{ $t('INBOX_MGMT.ADD.YANDEX.INVITE.TAKEN', { email: takenEmail }) }}
      </p>
    </div>
  </div>
</template>
